import { useEffect, useState } from 'react';
import { Modal, Box, Button, Checkbox, FormControlLabel } from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';

// ============================================
// 상수
// ============================================
// 로그인 액션이 세팅하는 1회성 플래그 (새로고침으로는 재노출되지 않게 소비 후 제거).
const SHOW_FLAG_KEY = 'showLoginNotice';
// 캠페인 구분자. 새 캠페인을 시작할 때 이 값을 바꾸면 이전 캠페인의 '안 보기' 선택이 초기화된다.
const CAMPAIGN_ID = '20260804';
// '오늘 하루 안 보기' 체크 시 저장하는 날짜(YYYY-MM-DD). 이 값이 오늘이면 노출하지 않는다.
const HIDE_UNTIL_KEY = `loginNoticeHideUntil.${CAMPAIGN_ID}`;
// '다음부터 안 보기' 체크 시 저장하는 플래그. 값이 '1'이면 캠페인 종료까지 다시 노출하지 않는다.
const HIDE_FOREVER_KEY = `loginNoticeHideForever.${CAMPAIGN_ID}`;

// 하드코딩 공지 이미지 목록. 순서대로 한 장씩 노출하며, 닫으면 다음 장이 바로 뜬다.
// endDate(YYYY-MM-DD, 포함)가 지난 이미지는 건너뛴다. 이미지마다 종료일을 다르게 둘 수 있다.
// public/images 아래에 파일을 두고 경로/종료일만 바꾸면 된다. (백엔드 불필요)
const NOTICE_IMAGES = [
  // 포인트 관리
  { src: '/images/login-notice-1.png', endDate: '2026-08-05' },
  // 그룹레슨 관리 & 그룹레슨 리뉴얼 출시 안내. 8/4부터 일주일간 노출.
  { src: '/images/login-notice-3.png', endDate: '2026-08-10' },
];

const todayString = () => dayjs().format('YYYY-MM-DD');

// ============================================
// Styled Components
// ============================================
const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '900px',
  maxWidth: '90vw',
  backgroundColor: '#fff',
  boxShadow: '0px 11px 15px -7px rgba(0,0,0,0.2)',
  borderRadius: '16px',
  overflow: 'hidden',
});

const NoticeImage = styled('img')({
  display: 'block',
  width: '100%',
  height: 'auto',
});

const FooterBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 24px',
  borderTop: '1px solid #f0f0f0',
});

const CheckboxGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
});

const HideTodayLabel = styled(FormControlLabel)({
  margin: 0,
  gap: '4px',
  '& .MuiCheckbox-root': {
    padding: '4px',
  },
  '& .MuiFormControlLabel-label': {
    fontSize: '13px',
    color: '#888',
    whiteSpace: 'nowrap',
  },
});

const CloseButton = styled(Button)({
  minWidth: '80px',
  height: '40px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#fff',
  backgroundColor: '#262626',
  '&:hover': {
    backgroundColor: '#000',
  },
});

// ============================================
// Main Component
// ============================================
/**
 * 로그인 직후 노출되는 공지 팝업.
 * - 로그인 액션이 sessionStorage(SHOW_FLAG_KEY)를 세팅하면 마운트 시 1회만 표시한다.
 * - 이미지를 여러 장 순차 노출한다. 한 장을 닫으면 다음 장이 바로 뜨고, 마지막 장을 닫으면 종료된다.
 * - 각 이미지의 endDate가 지나면 그 장은 건너뛰고, 남은 장이 없으면 노출하지 않는다.
 * - '오늘 하루 안 보기' 체크 후 닫으면 그날은, '다음부터 안 보기' 체크 후 닫으면 캠페인 종료까지 다시 표시하지 않는다.
 */
const LoginNoticeModal = () => {
  // 마운트 시점 기준으로 아직 종료되지 않은 이미지만 남긴다.
  const [images] = useState(() => NOTICE_IMAGES.filter(image => todayString() <= image.endDate));
  // -1 = 닫힘, 0..N-1 = 현재 노출 중인 이미지 인덱스.
  const [index, setIndex] = useState(-1);
  const [hideToday, setHideToday] = useState(false);
  const [hideForever, setHideForever] = useState(false);

  useEffect(() => {
    const triggered = sessionStorage.getItem(SHOW_FLAG_KEY) === '1';
    if (!triggered) return;
    // 새로고침으로 재노출되지 않도록 플래그를 즉시 소비.
    sessionStorage.removeItem(SHOW_FLAG_KEY);

    // 노출 기간이 남은 이미지가 없으면 띄우지 않는다.
    if (images.length === 0) return;
    // '다음부터 안 보기'를 선택했으면 다시 노출하지 않는다.
    if (localStorage.getItem(HIDE_FOREVER_KEY) === '1') return;
    // '오늘 하루 안 보기'가 오늘 날짜로 저장돼 있으면 노출하지 않는다.
    if (localStorage.getItem(HIDE_UNTIL_KEY) === todayString()) return;

    setIndex(0);
  }, [images]);

  // 두 옵션은 상호배타 — 하나를 켜면 다른 하나는 해제한다.
  const handleHideTodayChange = e => {
    const checked = e.target.checked;
    setHideToday(checked);
    if (checked) setHideForever(false);
  };
  const handleHideForeverChange = e => {
    const checked = e.target.checked;
    setHideForever(checked);
    if (checked) setHideToday(false);
  };

  const handleClose = () => {
    if (hideForever) {
      localStorage.setItem(HIDE_FOREVER_KEY, '1');
    } else if (hideToday) {
      localStorage.setItem(HIDE_UNTIL_KEY, todayString());
    }
    // '안 보기'를 체크했으면 남은 장을 건너뛰고 즉시 종료한다.
    if (hideForever || hideToday) {
      setIndex(-1);
      return;
    }
    // 다음 이미지가 있으면 이어서 노출, 없으면 종료.
    setIndex(prev => (prev + 1 < images.length ? prev + 1 : -1));
  };

  const open = index >= 0;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalContainer>
        {open && <NoticeImage src={images[index].src} alt={`공지 ${index + 1}`} />}
        <FooterBar>
          <CheckboxGroup>
            <HideTodayLabel
              control={
                <Checkbox size="small" checked={hideToday} onChange={handleHideTodayChange} />
              }
              label="오늘 하루 안 보기"
            />
            <HideTodayLabel
              control={
                <Checkbox size="small" checked={hideForever} onChange={handleHideForeverChange} />
              }
              label="다음부터 안 보기"
            />
          </CheckboxGroup>
          <CloseButton onClick={handleClose}>닫기</CloseButton>
        </FooterBar>
      </ModalContainer>
    </Modal>
  );
};

export default LoginNoticeModal;
